import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { db } from '../config/firebase';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';

const PaymentSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, userType } = useAuth();
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState<'success' | 'pending' | 'error' | null>(null);
  const [paymentData, setPaymentData] = useState<any>(null);

  const empresaId = searchParams.get('empresaId');
  const preferenceId = searchParams.get('preference_id');

  useEffect(() => {
    if (!isAuthenticated || userType !== 'Empresa') {
      navigate('/login');
      return;
    }

    const verificarPagamento = async () => {
      try {
        if (!empresaId || !preferenceId) {
          setPaymentStatus('error');
          setLoading(false);
          return;
        }

        // Buscar transação no Firestore
        const transacoesRef = collection(db, 'transacoesCarteira');
        const q = query(
          transacoesRef,
          where('referenciaPagamento', '==', preferenceId),
          where('empresaId', '==', empresaId)
        );

        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          const transacao = querySnapshot.docs[0].data();
          setPaymentData(transacao);
          
          if (transacao.status === 'aprovado') {
            setPaymentStatus('success');
          } else if (transacao.status === 'pendente') {
            setPaymentStatus('pending');
          } else {
            setPaymentStatus('error');
          }
        } else {
          setPaymentStatus('pending'); // Ainda processando
        }
      } catch (error) {
        console.error('Erro ao verificar pagamento:', error);
        setPaymentStatus('error');
      } finally {
        setLoading(false);
      }
    };

    verificarPagamento();
  }, [isAuthenticated, userType, empresaId, preferenceId, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Verificando pagamento...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
        {paymentStatus === 'success' && (
          <div className="text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-12 h-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Pagamento Aprovado!</h1>
            <p className="text-gray-600 mb-6">
              Seu pagamento foi processado com sucesso.
            </p>
            {paymentData && (
              <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                <div className="flex justify-between mb-2">
                  <span className="text-gray-600">Valor:</span>
                  <span className="font-semibold text-gray-900">
                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(paymentData.valor || 0)}
                  </span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-gray-600">Status:</span>
                  <span className="font-semibold text-green-600">Aprovado</span>
                </div>
                {paymentData.descricao && (
                  <div className="mt-2 pt-2 border-t border-gray-200">
                    <span className="text-sm text-gray-500">{paymentData.descricao}</span>
                  </div>
                )}
              </div>
            )}
            <button
              onClick={() => navigate('/empresa')}
              className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Voltar ao Dashboard
            </button>
          </div>
        )}

        {paymentStatus === 'pending' && (
          <div className="text-center">
            <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500"></div>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Pagamento em Processamento</h1>
            <p className="text-gray-600 mb-6">
              Seu pagamento está sendo processado. Você será notificado quando for confirmado.
            </p>
            <button
              onClick={() => navigate('/empresa')}
              className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Voltar ao Dashboard
            </button>
          </div>
        )}

        {paymentStatus === 'error' && (
          <div className="text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-12 h-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Erro no Pagamento</h1>
            <p className="text-gray-600 mb-6">
              Não foi possível verificar o status do pagamento. Entre em contato com o suporte se necessário.
            </p>
            <button
              onClick={() => navigate('/empresa')}
              className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Voltar ao Dashboard
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;


